import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

export interface UserProfile {
  _id?: string
  userId: string
  userName: string
  nickname?: string
  email: string
  bio?: string
  profileImageUrl?: string
  bannerImageUrl?: string
  xUrl?: string
  baseAppUrl?: string
  farcasterUrl?: string
  subscriptionType: 'free' | 'commenter' | 'project_owner'
  subscriptionStatus: 'active' | 'inactive' | 'cancelled' | 'past_due'
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  createdAt?: string
  updatedAt?: string
}

export const useUserProfile = (userId: string) => {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (userId) {
      fetchUserProfile(userId)
    }
  }, [userId])

  const fetchUserProfile = async (userIdParam: string) => {
    if (!userIdParam) return
    
    setLoading(true)
    try {
      const { list } = await lumi.entities.userProfiles.list({
        filter: { userId: userIdParam }
      })

      if (list.length > 0) {
        setProfile(list[0])
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const createUserProfile = async (profileData: Omit<UserProfile, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newProfile = await lumi.entities.userProfiles.create({
        ...profileData,
        creator: profileData.userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      setProfile(newProfile)
      return newProfile
    } catch (error) {
      console.error('Failed to create profile:', error)
      throw error
    }
  }

  const updateUserProfile = async (userId: string, updates: Partial<UserProfile>) => {
    try {
      const { list } = await lumi.entities.userProfiles.list({
        filter: { userId }
      })

      if (list.length > 0) {
        const updated = await lumi.entities.userProfiles.update(list[0]._id, {
          ...updates,
          updatedAt: new Date().toISOString()
        })
        setProfile(updated)
        return updated
      }
    } catch (error) {
      console.error('Failed to update profile:', error)
      throw error
    }
  }

  const canInteract = () => {
    if (!profile) return false
    return (
      profile.subscriptionType === 'commenter' || 
      profile.subscriptionType === 'project_owner'
    ) && profile.subscriptionStatus === 'active'
  }

  const canCreateProject = () => {
    if (!profile) return false
    return profile.subscriptionType === 'project_owner' && profile.subscriptionStatus === 'active'
  }

  const createOrUpdateProfile = async (updates: { nickname: string; bio: string; profileImageUrl: string; bannerImageUrl?: string; xUrl?: string; baseAppUrl?: string; farcasterUrl?: string }) => {
    if (!userId) throw new Error('User ID is required')
    
    try {
      // Check for duplicate nickname
      const { list: existingProfiles } = await lumi.entities.userProfiles.list({
        filter: { nickname: updates.nickname }
      })
      
      const { list } = await lumi.entities.userProfiles.list({
        filter: { userId }
      })

      if (list.length > 0) {
        // Update existing profile - check if nickname changed and if new nickname is taken
        if (list[0].nickname !== updates.nickname && existingProfiles.length > 0) {
          throw new Error('This username is already taken. Please choose a different one.')
        }
        
        const updated = await lumi.entities.userProfiles.update(list[0]._id, {
          nickname: updates.nickname,
          bio: updates.bio,
          profileImageUrl: updates.profileImageUrl,
          bannerImageUrl: updates.bannerImageUrl || list[0].bannerImageUrl,
          xUrl: updates.xUrl || list[0].xUrl,
          baseAppUrl: updates.baseAppUrl || list[0].baseAppUrl,
          farcasterUrl: updates.farcasterUrl || list[0].farcasterUrl,
          updatedAt: new Date().toISOString()
        })
        setProfile(updated)
        return updated
      } else {
        // Create new profile - check if nickname is already taken
        if (existingProfiles.length > 0) {
          throw new Error('This username is already taken. Please choose a different one.')
        }
        
        const newProfile = await lumi.entities.userProfiles.create({
          userId,
          userName: updates.nickname,
          email: '',
          nickname: updates.nickname,
          bio: updates.bio,
          profileImageUrl: updates.profileImageUrl,
          bannerImageUrl: updates.bannerImageUrl || '',
          xUrl: updates.xUrl || '',
          baseAppUrl: updates.baseAppUrl || '',
          farcasterUrl: updates.farcasterUrl || '',
          subscriptionType: 'free',
          subscriptionStatus: 'inactive',
          creator: userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
        setProfile(newProfile)
        return newProfile
      }
    } catch (error) {
      console.error('Failed to create or update profile:', error)
      throw error
    }
  }

  return {
    profile,
    loading,
    fetchUserProfile,
    createUserProfile,
    updateUserProfile,
    createOrUpdateProfile,
    canInteract,
    canCreateProject
  }
}
